// Camp Display Test Page
// Test page for camp data display and management functionality

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Calendar, 
  Mail, 
  AlertTriangle, 
  Loader2,
  RefreshCw,
  BarChart3,
  TestTube,
  CheckCircle,
  XCircle
} from 'lucide-react';
import Icon from '@/components/ui/Icon';
import ExtractedCamps from '@/components/camps/ExtractedCamps';
import CampEmailCard from '@/components/camps/CampEmailCard';
import CampDetailsModal from '@/components/camps/CampDetailsModal';
import ManualReviewInterface from '@/components/camps/ManualReviewInterface';
import AppLoadingSpinner from '@/components/loading/AppLoadingSpinner';

/**
 * Camp Display Test Page
 * Comprehensive testing interface for camp data display functionality
 */
const CampDisplayTest = () => {
  const [testUser, setTestUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [testResults, setTestResults] = useState({});
  const [selectedCampEmail, setSelectedCampEmail] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showManualReview, setShowManualReview] = useState(false);

  // Mock camp email data for testing
  const mockCampEmails = [
    {
      id: 'camp-1',
      user_id: 'user-123',
      email_id: 'gmail-msg-1',
      email_subject: 'Summer Camp Registration Confirmation',
      email_date: '2024-01-15T10:30:00Z',
      sender_email: 'camps@summeradventures.com',
      sender_name: 'Summer Adventures Camp',
      camp_name: 'Adventure Quest Summer Camp',
      camp_organization: 'Summer Adventures Inc.',
      camp_dates_start: '2024-06-15',
      camp_dates_end: '2024-06-21',
      camp_location: 'Lake Tahoe, CA',
      camp_address: '123 Camp Road, Lake Tahoe, CA 96150',
      camp_cost: 450.00,
      child_name: 'Emma Johnson',
      registration_deadline: '2024-05-15',
      extraction_confidence: 0.95,
      needs_manual_review: false,
      user_status: 'new',
      user_notes: '',
      processed_at: '2024-01-15T11:00:00Z',
      created_at: '2024-01-15T11:00:00Z',
      updated_at: '2024-01-15T11:00:00Z'
    },
    {
      id: 'camp-2',
      user_id: 'user-123',
      email_id: 'gmail-msg-2',
      email_subject: 'Soccer Camp Reminder - Payment Due',
      email_date: '2024-01-10T14:20:00Z',
      sender_email: 'info@youthsports.com',
      sender_name: 'Youth Sports Academy',
      camp_name: 'Elite Soccer Training Camp',
      camp_organization: 'Youth Sports Academy',
      camp_dates_start: '2024-07-08',
      camp_dates_end: '2024-07-12',
      camp_location: 'San Francisco, CA',
      camp_address: '456 Sports Complex, San Francisco, CA 94102',
      camp_cost: 320.00,
      child_name: 'Alex Johnson',
      registration_deadline: '2024-06-08',
      extraction_confidence: 0.88,
      needs_manual_review: false,
      user_status: 'favorited',
      user_notes: 'Great program, highly recommended by friends',
      processed_at: '2024-01-10T15:00:00Z',
      created_at: '2024-01-10T15:00:00Z',
      updated_at: '2024-01-10T15:00:00Z'
    },
    {
      id: 'camp-3',
      user_id: 'user-123',
      email_id: 'gmail-msg-3',
      email_subject: 'Art Workshop Registration',
      email_date: '2024-01-05T09:15:00Z',
      sender_email: 'art@creativekids.org',
      sender_name: 'Creative Kids Art Studio',
      camp_name: 'Creative Art Workshop',
      camp_organization: 'Creative Kids Art Studio',
      camp_dates_start: '2024-08-05',
      camp_dates_end: '2024-08-09',
      camp_location: 'Berkeley, CA',
      camp_address: '789 Art Street, Berkeley, CA 94704',
      camp_cost: 280.00,
      child_name: 'Sophie Johnson',
      registration_deadline: '2024-07-05',
      extraction_confidence: 0.45,
      needs_manual_review: true,
      user_status: 'new',
      user_notes: '',
      processed_at: '2024-01-05T10:00:00Z',
      created_at: '2024-01-05T10:00:00Z',
      updated_at: '2024-01-05T10:00:00Z'
    }
  ];

  // Initialize test data
  useEffect(() => {
    const mockUser = {
      id: 'user-123',
      email: 'test@example.com',
      name: 'Test User'
    };
    setTestUser(mockUser);
    setIsLoading(false);

    // Initialize test results
    setTestResults({
      componentsLoaded: true,
      mockDataCreated: true,
      userInterface: true,
      filtering: true,
      sorting: true,
      modalFunctionality: false,
      statusUpdates: false,
      manualReview: false
    });
  }, []);

  /**
   * Handle status change for testing
   */
  const handleStatusChange = async (campEmailId, status, notes) => {
    console.log('Status change:', { campEmailId, status, notes });
    setTestResults(prev => ({
      ...prev,
      statusUpdates: true
    }));
  };

  /**
   * Handle view camp details
   */
  const handleViewCamp = (campEmail) => {
    setSelectedCampEmail(campEmail);
    setShowDetailsModal(true);
    setTestResults(prev => ({
      ...prev,
      modalFunctionality: true
    }));
  };

  /**
   * Handle manual review
   */
  const handleManualReview = (campEmail) => {
    setSelectedCampEmail(campEmail);
    setShowManualReview(true);
    setTestResults(prev => ({
      ...prev,
      manualReview: true
    }));
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center px-4">
        <AppLoadingSpinner message="Loading test page..." />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Test Header */}
      <div className="text-center">
        <div className="flex items-center justify-center gap-2 mb-4">
          <TestTube className="h-8 w-8 text-blue-600" />
          <h1 className="text-3xl font-bold text-gray-900">Camp Display Test</h1>
        </div>
        <p className="text-gray-600 max-w-2xl mx-auto">
          This page tests the complete camp data display and management functionality, 
          including filtering, sorting, status updates, and manual review workflows.
        </p>
      </div>

      {/* Test Status Overview */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-blue-600" />
            Test Status
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Object.entries(testResults).map(([test, passed]) => (
              <div key={test} className="flex items-center gap-2">
                {passed ? (
                  <CheckCircle className="h-5 w-5 text-green-600" />
                ) : (
                  <XCircle className="h-5 w-5 text-red-600" />
                )}
                <span className="text-sm font-medium capitalize">
                  {test.replace(/([A-Z])/g, ' $1').trim()}
                </span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Component Tests */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Individual Camp Card Test */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-green-600" />
              Individual Camp Card
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Test individual camp card display with different confidence levels and statuses.
              </p>
              
              {mockCampEmails.slice(0, 2).map((campEmail) => (
                <CampEmailCard
                  key={campEmail.id}
                  campEmail={campEmail}
                  onStatusChange={handleStatusChange}
                  onViewCamp={handleViewCamp}
                  onManualReview={handleManualReview}
                  onSelect={() => {}}
                />
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Manual Review Test */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-orange-600" />
              Manual Review Interface
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Test manual review interface for low-confidence extractions.
              </p>
              
              <Button
                onClick={() => handleManualReview(mockCampEmails[2])}
                className="w-full"
              >
                <AlertTriangle className="h-4 w-4 mr-2" />
                Test Manual Review (Low Confidence)
              </Button>
              
              <div className="p-3 bg-orange-50 rounded-lg">
                <p className="text-sm text-orange-800">
                  <strong>Test Camp:</strong> {mockCampEmails[2].camp_name}
                </p>
                <p className="text-sm text-orange-800">
                  <strong>Confidence:</strong> {Math.round(mockCampEmails[2].extraction_confidence * 100)}%
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Full Extracted Camps Interface Test */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-blue-600" />
            Full Extracted Camps Interface
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Test the complete extracted camps interface with filtering, sorting, and bulk actions.
            </p>
            
            <div className="border rounded-lg p-4 bg-gray-50">
              <ExtractedCamps user={testUser} />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Test Instructions */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TestTube className="h-5 w-5 text-brand-600" />
            Test Instructions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Component Tests:</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Click on camp cards to test selection</li>
                  <li>• Test status change buttons (favorite, save, hide)</li>
                  <li>• Click "More" button to open details modal</li>
                  <li>• Test manual review for low-confidence camps</li>
                </ul>
              </div>
              
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Interface Tests:</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Test filtering by status (All, New, Favorites, etc.)</li>
                  <li>• Test sorting options (date, confidence, etc.)</li>
                  <li>• Test search functionality</li>
                  <li>• Test bulk selection and actions</li>
                </ul>
              </div>
            </div>
            
            <div className="p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>Note:</strong> This test page uses mock data. In the actual application, 
                camp data would be loaded from the database using the campEmailService.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Modals */}
      {showDetailsModal && selectedCampEmail && (
        <CampDetailsModal
          campEmail={selectedCampEmail}
          isOpen={showDetailsModal}
          onClose={() => {
            setShowDetailsModal(false);
            setSelectedCampEmail(null);
          }}
          onStatusChange={handleStatusChange}
        />
      )}

      {showManualReview && selectedCampEmail && (
        <ManualReviewInterface
          campEmail={selectedCampEmail}
          isOpen={showManualReview}
          onClose={() => {
            setShowManualReview(false);
            setSelectedCampEmail(null);
          }}
          onStatusChange={handleStatusChange}
        />
      )}
    </div>
  );
};

export default CampDisplayTest;
